import React, { useRef, useEffect, useState } from 'react';

// Dynamically import Capacitor for mobile apps
let Camera: any = null;
let Capacitor: any = null;

try {
  const capacitorCore = require('@capacitor/core');
  const capacitorCamera = require('@capacitor/camera');
  Capacitor = capacitorCore.Capacitor;
  Camera = capacitorCamera.Camera;
} catch (e) {
  // Capacitor not available (web environment)
}

interface CameraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (imageDataUrl: string) => void;
  title?: string;
}

const CameraModal: React.FC<CameraModalProps> = ({
  isOpen,
  onClose,
  onCapture,
  title = 'Take Photo',
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState('');
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const isNative = Capacitor && Capacitor.isNativePlatform();

  useEffect(() => {
    if (isOpen && !isNative) {
      startCamera();
    } else if (!isOpen) {
      stopCamera();
      setCapturedImage(null);
      setIsPreviewMode(false);
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, facingMode, isNative]);

  const startCamera = async () => {
    try {
      stopCamera(); // Stop any existing stream first

      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setError('');
    } catch (err: any) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhotoWeb = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0);
        const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setCapturedImage(imageDataUrl);
        setIsPreviewMode(true);
        stopCamera();
      }
    }
  };

  const capturePhotoNative = async () => {
    if (!Camera) {
      setError('Camera plugin not available');
      return;
    }
    
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: 'base64',
        source: 'CAMERA',
        direction: facingMode === 'environment' ? 'BACK' : 'FRONT',
      });

      if (image.base64String) {
        const imageDataUrl = `data:image/jpeg;base64,${image.base64String}`;
        setCapturedImage(imageDataUrl);
        setIsPreviewMode(true);
      }
    } catch (err: any) {
      console.error('Error capturing photo:', err);
      if (err.message !== 'User cancelled') {
        setError('Failed to capture photo. Please try again.');
      }
    }
  };

  const handleCapture = () => {
    if (isNative) {
      capturePhotoNative();
    } else {
      capturePhotoWeb();
    }
  };

  const handleConfirm = () => {
    if (capturedImage) {
      onCapture(capturedImage);
      setCapturedImage(null);
      setIsPreviewMode(false);
      onClose();
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setIsPreviewMode(false);
    if (!isNative) {
      startCamera();
    }
  };

  const switchCamera = () => {
    if (!isNative) {
      setFacingMode(facingMode === 'user' ? 'environment' : 'user');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={() => {
              stopCamera();
              setCapturedImage(null);
              setIsPreviewMode(false);
              onClose();
            }}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {!isPreviewMode ? (
          <>
            {/* Camera View */}
            <div className="relative bg-black rounded-lg overflow-hidden mb-4 flex-1 flex items-center justify-center" style={{ minHeight: '400px' }}>
              {!isNative && (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
              )}
              {isNative && (
                <div className="text-white text-center p-8">
                  <p className="mb-4">Tap the capture button to take a photo</p>
                  <i className="ri-camera-line text-6xl"></i>
                </div>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <div className="flex space-x-3">
              {!isNative && (
                <button
                  onClick={switchCamera}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
                >
                  🔄 Switch
                </button>
              )}
              <button
                onClick={handleCapture}
                className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition font-medium"
              >
                📸 Capture
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Preview Mode */}
            <div 
              className="relative bg-black rounded-lg overflow-hidden mb-4 flex-1 flex items-center justify-center"
              style={{ minHeight: '400px' }}
            >
              <img
                src={capturedImage || ''}
                alt="Captured"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleRetake}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition font-medium"
              >
                ↺ Retake
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition font-medium"
              >
                ✓ Confirm
              </button>
            </div>
          </>
        )}

        <button
          onClick={() => {
            stopCamera();
            setCapturedImage(null);
            setIsPreviewMode(false);
            onClose();
          }}
          className="w-full mt-3 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default CameraModal;
